import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  FiCamera, FiLink, FiLock, FiUnlock, FiSend, FiCheck, 
  FiExternalLink, FiCopy, FiRefreshCw 
} from 'react-icons/fi';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function PhotoGalleryManager({ reservationId, reservation, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState({
    url: '',
    password: '',
    enabled: true,
    sendNotification: true
  });

  useEffect(() => {
    if (reservation) {
      setGallery({
        url: reservation.photo_gallery_url || '',
        password: reservation.photo_gallery_password || '',
        enabled: reservation.photo_gallery_enabled ?? true,
        sendNotification: !reservation.photo_gallery_url // Only send if new
      });
    }
  }, [reservation]);

  const handleSave = async () => {
    if (!gallery.url.trim()) {
      toast.error('Veuillez entrer un lien vers la galerie');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/reservations/${reservationId}/gallery`, {
        gallery_url: gallery.url,
        gallery_password: gallery.password || null,
        gallery_enabled: gallery.enabled,
        send_notification: gallery.sendNotification
      });

      if (response.data.success) {
        toast.success('Galerie photos mise à jour');
        if (gallery.sendNotification) {
          toast.info('Email envoyé au client');
        }
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const hasExistingGallery = Boolean(reservation?.photo_gallery_url);

  return (
    <Card className="border-slate-200" data-testid="photo-gallery-manager">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FiCamera className="h-5 w-5 text-purple-600" />
          Galerie Photos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <p className="text-sm text-purple-700">
            Partagez les photos de l'événement avec votre client. 
            Le lien sera visible dans son espace personnel.
          </p>
        </div>

        {/* Gallery URL */}
        <div className="space-y-2">
          <Label htmlFor="gallery-url" className="text-sm font-medium">
            Lien de la galerie *
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <FiLink className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="gallery-url"
                type="url"
                placeholder="https://drive.google.com/... ou autre service"
                value={gallery.url}
                onChange={(e) => setGallery({ ...gallery, url: e.target.value })}
                className="pl-10"
                data-testid="gallery-url-input"
              />
            </div>
            {gallery.url && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => window.open(gallery.url, '_blank')}
                title="Ouvrir le lien"
              >
                <FiExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Google Drive, Dropbox, WeTransfer, ou tout autre service de partage
          </p>
        </div>

        {/* Password Protection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="gallery-password" className="text-sm font-medium flex items-center gap-2">
              {gallery.password ? (
                <FiLock className="h-4 w-4 text-amber-600" />
              ) : (
                <FiUnlock className="h-4 w-4 text-slate-400" />
              )}
              Mot de passe (optionnel)
            </Label>
            {gallery.password && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(gallery.password, 'Mot de passe')}
                className="h-6 text-xs"
              >
                <FiCopy className="h-3 w-3 mr-1" />
                Copier
              </Button>
            )}
          </div>
          <Input
            id="gallery-password"
            type="text"
            placeholder="Laisser vide si pas de protection"
            value={gallery.password}
            onChange={(e) => setGallery({ ...gallery, password: e.target.value })}
            data-testid="gallery-password-input"
          />
          {gallery.password && (
            <p className="text-xs text-amber-600">
              Le mot de passe sera affiché au client dans son espace
            </p>
          )}
        </div>

        {/* Toggle options */}
        <div className="space-y-3 pt-2">
          {/* Enable/Disable gallery */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm font-medium">Activer la galerie</p>
              <p className="text-xs text-slate-500">Rendre le lien visible au client</p>
            </div>
            <Switch
              checked={gallery.enabled}
              onCheckedChange={(checked) => setGallery({ ...gallery, enabled: checked })}
              data-testid="gallery-enabled-switch"
            />
          </div>

          {/* Send notification */}
          {gallery.enabled && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <FiSend className="h-4 w-4 text-blue-600" />
                  Notifier le client par email
                </p>
                <p className="text-xs text-slate-500">
                  {hasExistingGallery 
                    ? "Renvoyer une notification" 
                    : "Envoyer un email avec le lien"
                  }
                </p>
              </div>
              <Switch
                checked={gallery.sendNotification}
                onCheckedChange={(checked) => setGallery({ ...gallery, sendNotification: checked })}
                data-testid="send-notification-switch"
              />
            </div>
          )}
        </div>

        {/* Existing gallery info */}
        {hasExistingGallery && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <FiCheck className="h-4 w-4" />
              <span className="text-sm font-medium">Galerie déjà configurée</span>
            </div>
            <p className="text-xs text-green-600 mt-1">
              Mise à jour le {new Date(reservation.photo_gallery_updated_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
        )}

        {/* Save button */}
        <Button
          onClick={handleSave}
          disabled={saving || !gallery.url.trim()}
          className="w-full bg-purple-600 hover:bg-purple-700"
          data-testid="save-gallery-btn"
        >
          {saving ? (
            <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FiCamera className="mr-2 h-4 w-4" />
          )}
          {hasExistingGallery ? 'Mettre à jour la galerie' : 'Enregistrer la galerie'}
        </Button>
      </CardContent>
    </Card>
  );
}
